import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from 'vue';

export interface PageSectionNavItem {
  id: string;
  label: string;
  /** Section root card element */
  el: HTMLElement;
  /** Section header element used for scrollspy / scroll-into-view */
  headerEl: HTMLElement;
  expand?: () => void;
}

export interface PageSectionNavApi {
  register: (item: PageSectionNavItem) => void;
  unregister: (id: string) => void;
  update: (
    id: string,
    patch: Partial<Pick<PageSectionNavItem, 'label' | 'el' | 'headerEl' | 'expand'>>,
  ) => void;
  sections: ComputedRef<PageSectionNavItem[]>;
  activeId: Ref<string | null>;
  isVisible: ComputedRef<boolean>;
  scrollTo: (id: string) => void;
}

export const PAGE_SECTION_NAV_KEY: InjectionKey<PageSectionNavApi> =
  Symbol('pageSectionNav');

let sectionUid = 0;

export function createSectionId(prefix = 'page-section'): string {
  sectionUid += 1;
  return `${prefix}-${sectionUid}`;
}

function getScrollTop(): number {
  if (typeof document === 'undefined') {
    return 0;
  }

  return (document.scrollingElement || document.documentElement).scrollTop;
}

function getDocumentTop(el: HTMLElement): number {
  return el.getBoundingClientRect().top + getScrollTop();
}

function getHeaderOffset(): number {
  if (typeof document === 'undefined') {
    return 96;
  }

  const header = document.getElementById('kt_header');
  const headerHeight = header?.getBoundingClientRect().height ?? 0;

  // Activation line sits just below the fixed page header.
  return Math.max(headerHeight, 64) + 12;
}

function sortByDocumentOrder(items: PageSectionNavItem[]): PageSectionNavItem[] {
  return [...items].sort((a, b) => {
    if (a.headerEl === b.headerEl) {
      return 0;
    }

    const position = a.headerEl.compareDocumentPosition(b.headerEl);

    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    }

    if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }

    return 0;
  });
}

/**
 * Provides section-nav registry + scrollspy for PageContentBase (plain).
 * Navigator is shown only when 2+ labeled sections are registered.
 * Active section follows document scroll vs each section header.
 */
export function providePageSectionNav(): PageSectionNavApi {
  const items = ref<PageSectionNavItem[]>([]);
  const activeId = ref<string | null>(null);
  let scrollLocked = false;
  let unlockTimer: ReturnType<typeof setTimeout> | null = null;
  let rafId = 0;

  const sections = computed(() =>
    sortByDocumentOrder(
      items.value.filter((item) => item.label.trim().length > 0),
    ),
  );

  const isVisible = computed(() => sections.value.length > 1);

  const syncActiveFromScroll = () => {
    if (scrollLocked || !isVisible.value) {
      return;
    }

    const list = sections.value;
    if (list.length === 0) {
      activeId.value = null;
      return;
    }

    const scrollTop = getScrollTop();
    const viewportBottom = scrollTop + window.innerHeight;
    const docHeight =
      document.scrollingElement?.scrollHeight ||
      document.documentElement.scrollHeight;
    const nearBottom = viewportBottom >= docHeight - 4;

    // When at (or near) the bottom, pin the last section.
    if (nearBottom) {
      activeId.value = list[list.length - 1].id;
      return;
    }

    const activationY = scrollTop + getHeaderOffset();
    let current = list[0].id;

    for (const section of list) {
      const headerTop = getDocumentTop(section.headerEl);
      // Header has crossed the activation line under the fixed header.
      if (headerTop <= activationY + 1) {
        current = section.id;
      } else {
        break;
      }
    }

    activeId.value = current;
  };

  const onScrollOrResize = () => {
    if (rafId) {
      return;
    }

    rafId = window.requestAnimationFrame(() => {
      rafId = 0;
      syncActiveFromScroll();
    });
  };

  const register = (item: PageSectionNavItem) => {
    const index = items.value.findIndex((entry) => entry.id === item.id);
    if (index >= 0) {
      items.value.splice(index, 1, item);
    } else {
      items.value.push(item);
    }

    if (!activeId.value && item.label.trim()) {
      activeId.value = item.id;
    }

    syncActiveFromScroll();
  };

  const unregister = (id: string) => {
    items.value = items.value.filter((entry) => entry.id !== id);
    if (activeId.value === id) {
      activeId.value = sections.value[0]?.id ?? null;
    }
    syncActiveFromScroll();
  };

  const update = (
    id: string,
    patch: Partial<Pick<PageSectionNavItem, 'label' | 'el' | 'headerEl' | 'expand'>>,
  ) => {
    const index = items.value.findIndex((entry) => entry.id === id);
    if (index < 0) {
      return;
    }

    items.value.splice(index, 1, {
      ...items.value[index],
      ...patch,
    });
    syncActiveFromScroll();
  };

  const scrollTo = (id: string) => {
    const target = items.value.find((entry) => entry.id === id);
    if (!target) {
      return;
    }

    target.expand?.();
    activeId.value = id;
    scrollLocked = true;

    if (unlockTimer) {
      clearTimeout(unlockTimer);
    }

    const top = Math.max(
      getDocumentTop(target.headerEl) - getHeaderOffset(),
      0,
    );

    window.scrollTo({ top, behavior: 'smooth' });

    unlockTimer = setTimeout(() => {
      scrollLocked = false;
      syncActiveFromScroll();
    }, 800);
  };

  onMounted(() => {
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    document.addEventListener('scroll', onScrollOrResize, {
      passive: true,
      capture: true,
    });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    syncActiveFromScroll();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScrollOrResize);
    document.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);

    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }

    if (unlockTimer) {
      clearTimeout(unlockTimer);
    }
  });

  const api: PageSectionNavApi = {
    register,
    unregister,
    update,
    sections,
    activeId,
    isVisible,
    scrollTo,
  };

  provide(PAGE_SECTION_NAV_KEY, api);

  return api;
}

export function usePageSectionNav(): PageSectionNavApi | null {
  return inject(PAGE_SECTION_NAV_KEY, null);
}
